import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Radio,
    Tooltip,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ICustomField, FieldType, ICampoAssociacaoAPI } from './types';
import { useAlert } from '../../../modules/services/Bundle';
import { useAuth } from '../../../modules/auth';

// Tipos e constantes para máscaras pré-configuradas
interface MaskOption {
    value: string;
    label: string;
    pattern: string; // Padrão para react-input-mask
    description: string;
}

const PREDEFINED_MASKS: Record<string, MaskOption> = {
    'CPF': {
        value: '999.999.999-99',
        label: 'CPF',
        pattern: '999.999.999-99',
        description: 'CPF: 000.000.000-00'
    },
    'CNPJ': {
        value: '99.999.999/9999-99',
        label: 'CNPJ',
        pattern: '99.999.999/9999-99',
        description: 'CNPJ: 00.000.000/0000-00'
    },
    'TELEFONE_FIXO': {
        value: '(99) 9999-9999',
        label: 'Telefone Fixo',
        pattern: '(99) 9999-9999',
        description: 'Telefone: (00) 0000-0000'
    },
    'TELEFONE_CELULAR': {
        value: '(99) 99999-9999',
        label: 'Telefone Celular',
        pattern: '(99) 99999-9999',
        description: 'Celular: (00) 00000-0000'
    },
    'CEP': {
        value: '99999-999',
        label: 'CEP',
        pattern: '99999-999',
        description: 'CEP: 00000-000'
    },
    'PLACA_ANTIGA': {
        value: 'AAA-9999',
        label: 'Placa Antiga',
        pattern: 'AAA-9999',
        description: 'Placa: AAA-0000'
    },
    'PLACA_MERCOSUL': {
        value: 'AAA9A99',
        label: 'Placa Mercosul',
        pattern: 'AAA9A99',
        description: 'Placa: AAA0A00'
    },
    'DATA': {
        value: '99/99/9999',
        label: 'Data',
        pattern: '99/99/9999',
        description: 'Data: DD/MM/YYYY'
    },
    'HORA': {
        value: '99:99',
        label: 'Hora',
        pattern: '99:99',
        description: 'Hora: HH:MM'
    },
    'DATA_HORA': {
        value: '99/99/9999 99:99',
        label: 'Data e Hora',
        pattern: '99/99/9999 99:99',
        description: 'Data e Hora: DD/MM/YYYY HH:MM'
    }
};

// Função para obter máscara padrão baseada no tipo de campo
const getDefaultMaskForType = (tipo: FieldType): string => {
    switch (tipo) {
        case 'Numérico':
        case 'Decimal':
            return ''; // Sem máscara, apenas números serão aceitos
        case 'Data':
            return PREDEFINED_MASKS.DATA.value;
        case 'Texto':
        case 'Booleano':
        default:
            return ''; // Sem máscara padrão para texto
    }
};

interface FieldConfigurationProps {
    organizationId?: number;
    organizationName?: string;
    corporationName: string;
    applicationId?: string;
}

const FieldConfiguration: React.FC<FieldConfigurationProps> = ({
    organizationId,
    organizationName,
    corporationName,
    applicationId = process.env.REACT_APP_POWER_BI_APPLICATIONID,
}) => {
    const alert = useAlert();
    const { auth } = useAuth();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const [fields, setFields] = useState<ICustomField[]>([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingField, setEditingField] = useState<ICustomField | null>(null);
    const [formData, setFormData] = useState<Omit<ICustomField, 'Id' | 'Chave' | 'IdOrganization' | 'IdApplication'>>({
        Campo: '',
        Descricao: '',
        Tipo: 'Texto',
        Tamanho: 50,
        Mascara: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [selectedMaskType, setSelectedMaskType] = useState<string>('CUSTOM'); // 'CUSTOM' ou uma das chaves de PREDEFINED_MASKS

    const buildGetCamposEndpoint = () => {
        if (!backendUrl || !corporationName) return null;
        const encodedCorporacao = encodeURIComponent(corporationName);

        if (organizationName) {
            const encodedOrganizacao = encodeURIComponent(organizationName);
            return `${backendUrl}/api/associacao/campos/corporacao${encodedCorporacao}/organizacao${encodedOrganizacao}`;
        }

        return `${backendUrl}/api/associacao/campos/corporacao${encodedCorporacao}`;
    };

    const buildMutateCamposEndpoint = () => {
        if (!backendUrl || !corporationName) return null;
        const encodedCorporacao = encodeURIComponent(corporationName);

        if (organizationName) {
            const encodedOrganizacao = encodeURIComponent(organizationName);
            return `${backendUrl}/api/associacao/campos/corporacao/${encodedCorporacao}/organizacao/${encodedOrganizacao}`;
        }

        return `${backendUrl}/api/associacao/campos/corporacao/${encodedCorporacao}`;
    };

    const buildCampoLabelEndpoint = (labelKey: string) => {
        const base = buildMutateCamposEndpoint();
        if (!base) return null;
        const encodedLabelKey = encodeURIComponent(labelKey);
        return `${base}labelkey/${encodedLabelKey}`;
    };

    // Função para mapear FieldType para o formato da API
    // A API aceita apenas "Número" ou "Texto" conforme a documentação
    const mapFieldTypeToAPI = (tipo: FieldType): string => {
        switch (tipo) {
            case 'Numérico':
            case 'Decimal':
                return 'Número';
            case 'Texto':
            case 'Data':
            case 'Booleano':
            default:
                return 'Texto';
        }
    };

    // Função para mapear do formato da API para FieldType
    const mapAPIToFieldType = (tipo: string): FieldType => {
        if (tipo === 'Número' || tipo === 'Numérico') {
            return 'Numérico';
        }
        return 'Texto';
    };

    // Converter ICustomField para ICampoAssociacaoAPI
    const convertToAPIFormat = (field: ICustomField): ICampoAssociacaoAPI => {
        return {
            LabelKey: field.Campo,
            LabelValue: field.Descricao,
            LabelType: mapFieldTypeToAPI(field.Tipo),
            LabelLength: field.Tamanho,
            LabelMask: field.Mascara || undefined,
            IsKeyColumn: field.Chave ? 1 : 0,
        };
    };

    // Converter ICampoAssociacaoAPI para ICustomField
    const convertFromAPIFormat = (apiField: ICampoAssociacaoAPI, id?: number): ICustomField => {
        // Converter IsKeyColumn de número (0/1) para boolean se necessário
        const isKeyColumn = typeof apiField.IsKeyColumn === 'number' 
            ? apiField.IsKeyColumn === 1 
            : apiField.IsKeyColumn;

        return {
            Id: id,
            Campo: apiField.LabelKey,
            Descricao: apiField.LabelValue,
            Tipo: mapAPIToFieldType(apiField.LabelType),
            Tamanho: apiField.LabelLength,
            Mascara: apiField.LabelMask,
            Chave: isKeyColumn,
        };
    };

    // Buscar campos ao montar o componente
    useEffect(() => {
        if (corporationName) {
            fetchFields();
        }
    }, [organizationId, organizationName, corporationName, backendUrl]);

    const fetchFields = async () => {
        const camposEndpoint = buildGetCamposEndpoint();
        if (!camposEndpoint) return;

        setLoading(true);
        try {
            // Endpoint correto conforme documentação Swagger
            // GET /api/associacao/campos/corporacao{corporacao}/organizacao{organizacao}
            // Formato esperado: /api/associacao/campos/corporacao{corporacao}/organizacao{organizacao} (sem barras)
            const response = await fetch(
                camposEndpoint,
                {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${auth?.Token}`
                    }
                }
            );

            if (!response.ok) {
                // Se o erro for 404, pode ser que não existam campos ainda, então não mostrar erro
                if (response.status !== 404) {
                    throw new Error(`Erro ao buscar campos: ${response.statusText}`);
                }
                setFields([]);
                return;
            }

            const data = await response.json();
            console.log('Resposta da API de campos:', data);

            // A resposta pode estar diretamente como array ou dentro de um objeto
            let apiFields: ICampoAssociacaoAPI[] = [];
            
            if (Array.isArray(data)) {
                apiFields = data;
            } else if (data && typeof data === 'object') {
                // Se for um objeto, pode ter uma propriedade que contém o array
                const possibleArrays = Object.values(data).filter(Array.isArray);
                if (possibleArrays.length > 0) {
                    apiFields = possibleArrays[0] as ICampoAssociacaoAPI[];
                }
            }

            console.log('Campos da API processados:', apiFields);

            // Converter do formato da API para o formato interno
            // Usar o hash do LabelKey como ID único, já que LabelKey é único por corporação/organização
            const convertedFields = apiFields.map((apiField: ICampoAssociacaoAPI) => {
                // Criar um ID único baseado no LabelKey usando hash simples
                const fieldId = apiField.LabelKey.split('').reduce((acc, char) => {
                    return ((acc << 5) - acc) + char.charCodeAt(0);
                }, 0);
                return convertFromAPIFormat(apiField, Math.abs(fieldId));
            });

            console.log('Campos convertidos para exibição:', convertedFields);
            setFields(convertedFields);
        } catch (error) {
            console.error('Erro ao buscar campos:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                message: 'Erro ao carregar campos personalizados',
            });
            setFields([]);
        } finally {
            setLoading(false);
        }
    };

    // Função para validar formato de máscara
    const validateMaskFormat = (mask: string): string | null => {
        if (!mask) return null; // Máscara é opcional
        
        // Validar caracteres permitidos na máscara
        const validMaskChars = /^[9Aa*\-_\/\(\)\.\s:]+$/;
        if (!validMaskChars.test(mask)) {
            return 'A máscara contém caracteres inválidos. Use apenas: 9, A, a, *, -, _, /, (, ), ., espaços e :';
        }

        // Validar se há pelo menos um caractere de padrão (9, A, a, *)
        const hasPattern = /[9Aa*]/.test(mask);
        if (!hasPattern) {
            return 'A máscara deve conter pelo menos um caractere de padrão (9, A, a ou *)';
        }

        return null;
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.Campo.trim()) {
            newErrors.Campo = 'O nome do campo (LabelKey) é obrigatório';
        } else {
            // Validar se o LabelKey já existe em outro campo (exceto o que está sendo editado)
            const campoExists = fields.some(
                (f) => f.Campo.toLowerCase() === formData.Campo.trim().toLowerCase() && f.Id !== editingField?.Id
            );
            if (campoExists) {
                newErrors.Campo = 'Já existe um campo com este nome (LabelKey deve ser único)';
            }
        }

        if (!formData.Descricao.trim()) {
            newErrors.Descricao = 'A descrição (LabelValue) é obrigatória';
        }

        if (formData.Tamanho <= 0) {
            newErrors.Tamanho = 'O tamanho deve ser maior que zero';
        }

        if (formData.Tipo === 'Texto' && formData.Tamanho > 1000) {
            newErrors.Tamanho = 'O tamanho máximo para texto é 1000';
        }

        // Validar máscara se fornecida
        if (formData.Mascara) {
            const maskError = validateMaskFormat(formData.Mascara);
            if (maskError) {
                newErrors.Mascara = maskError;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Função para identificar se uma máscara corresponde a uma pré-configurada
    const identifyMaskType = (mask: string): string => {
        if (!mask) return 'CUSTOM';
        const found = Object.entries(PREDEFINED_MASKS).find(([_, option]) => option.value === mask);
        return found ? found[0] : 'CUSTOM';
    };

    const handleOpenDialog = (field?: ICustomField) => {
        if (field) {
            setEditingField(field);
            const maskType = identifyMaskType(field.Mascara || '');
            setSelectedMaskType(maskType);
            setFormData({
                Campo: field.Campo,
                Descricao: field.Descricao,
                Tipo: field.Tipo,
                Tamanho: field.Tamanho,
                Mascara: field.Mascara || '',
            });
        } else {
            setEditingField(null);
            setSelectedMaskType('CUSTOM');
            setFormData({
                Campo: '',
                Descricao: '',
                Tipo: 'Texto',
                Tamanho: 50,
                Mascara: '',
            });
        }
        setErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingField(null);
        setErrors({});
    };

    const handleSaveField = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            // Se estiver editando, o LabelKey não pode ser alterado
            const labelKeyChanged = editingField && editingField.Campo !== formData.Campo.trim();
            if (labelKeyChanged) {
                alert.createMessage({
                    icon: 'warning',
                    title: 'LabelKey inválido',
                    message: 'Não é permitido alterar o LabelKey de um campo existente. Crie um novo campo com o LabelKey desejado.',
                });
                return;
            }

            // Criar campo no formato interno
            const isEditing = !!editingField;

            const field: ICustomField = {
                ...formData,
                Id: editingField?.Id,
                Chave: editingField?.Chave || false, // Manter o valor atual da chave
                IdOrganization: organizationId,
                IdApplication: applicationId ? parseInt(applicationId) : undefined,
            };

            // Converter para o formato da API
            const apiPayload = convertToAPIFormat(field);

            const targetEndpoint = isEditing
                ? buildCampoLabelEndpoint(editingField!.Campo)
                : buildMutateCamposEndpoint();

            if (!targetEndpoint) {
                throw new Error('Endpoint inválido para salvar campo');
            }

            const method = isEditing ? 'PUT' : 'POST';

            const response = await fetch(
                targetEndpoint,
                {
                    method,
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json-patch+json',
                        'Authorization': `Bearer ${auth?.Token}`
                    },
                    body: JSON.stringify(apiPayload)
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Erro ao salvar campo');
            }

            alert.createMessage({
                icon: 'success',
                title: 'Sucesso',
                message: `Campo ${editingField ? 'atualizado' : 'criado'} com sucesso!`,
            });
            fetchFields();
            handleCloseDialog();
        } catch (error) {
            console.error('Erro ao salvar campo:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                message: `Erro ao ${editingField ? 'atualizar' : 'criar'} campo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteField = async (field: ICustomField) => {
        if (!field.Campo) return;

        if (!window.confirm(`Tem certeza que deseja deletar o campo "${field.Campo}"?`)) return;

        setLoading(true);
        try {
            // Codificar os nomes e o LabelKey para URL
            const deleteEndpoint = buildCampoLabelEndpoint(field.Campo);
            if (!deleteEndpoint) {
                throw new Error('Endpoint inválido para deletar campo');
            }

            // Assumindo que o DELETE usa o LabelKey como identificador
            const response = await fetch(
                deleteEndpoint,
                {
                    method: 'DELETE',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${auth?.Token}`
                    }
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Erro ao deletar campo');
            }

            alert.createMessage({
                icon: 'success',
                title: 'Sucesso',
                message: 'Campo deletado com sucesso!',
            });
            fetchFields();
        } catch (error) {
            console.error('Erro ao deletar campo:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                message: `Erro ao deletar campo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleKeyChange = async (fieldId: number | undefined) => {
        if (!fieldId) return;

        const fieldToUpdate = fields.find((f) => f.Id === fieldId);
        if (!fieldToUpdate) return;

        setLoading(true);
        try {
            // Atualizar o campo selecionado para ser chave e os outros para não serem
            const updatedFields = fields.map((f) => ({
                ...f,
                Chave: f.Id === fieldId,
            }));

            // Atualizar apenas o campo que foi selecionado como chave
            const field = updatedFields.find((f) => f.Id === fieldId);
            if (field) {
                const apiPayload = convertToAPIFormat(field);
                const updateEndpoint = buildCampoLabelEndpoint(field.Campo);
                if (!updateEndpoint) {
                    throw new Error('Endpoint inválido para atualizar chave');
                }

                const response = await fetch(
                    updateEndpoint,
                    {
                        method: 'PUT',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json-patch+json',
                            'Authorization': `Bearer ${auth?.Token}`
                        },
                        body: JSON.stringify(apiPayload)
                    }
                );

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(errorText || 'Erro ao atualizar chave');
                }

                // Atualizar os outros campos para não serem chave
                // Nota: A API pode não suportar atualização em lote, então atualizamos apenas o selecionado
                // Os outros campos serão atualizados quando o usuário selecionar outro como chave
                setFields(updatedFields);
                alert.createMessage({
                    icon: 'success',
                    title: 'Sucesso',
                    message: 'Chave atualizada com sucesso!',
                });
                // Recarregar os campos para garantir sincronização
                fetchFields();
            }
        } catch (error) {
            console.error('Erro ao atualizar chave:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                message: `Erro ao atualizar chave: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                    Configuração de Campos Personalizados
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    disabled={loading}
                >
                    Adicionar Campo
                </Button>
            </Box>

            {loading && fields.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                                    Campo
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                                    Descrição
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                                    Tipo
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                                    Tamanho
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                                    Máscara
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                                    Chave
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'primary.main' }}>
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fields.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Nenhum campo cadastrado. Clique em "Adicionar Campo" para começar.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                fields.map((field) => (
                                    <TableRow key={field.Id} hover>
                                        <TableCell>{field.Campo}</TableCell>
                                        <TableCell>{field.Descricao}</TableCell>
                                        <TableCell>{field.Tipo}</TableCell>
                                        <TableCell>{field.Tamanho}</TableCell>
                                        <TableCell>{field.Mascara || '-'}</TableCell>
                                        <TableCell align="center">
                                            <Radio
                                                checked={field.Chave}
                                                onChange={() => handleKeyChange(field.Id)}
                                                disabled={loading}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Editar">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleOpenDialog(field)}
                                                    disabled={loading}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Deletar">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteField(field)}
                                                    disabled={loading}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog para Adicionar/Editar Campo */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingField ? 'Editar Campo' : 'Adicionar Novo Campo'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Campo *"
                            value={formData.Campo}
                            onChange={(e) => setFormData({ ...formData, Campo: e.target.value })}
                            error={!!errors.Campo}
                            helperText={errors.Campo}
                            placeholder="Ex: Veículo"
                        />

                        <TextField
                            fullWidth
                            label="Descrição *"
                            value={formData.Descricao}
                            onChange={(e) => setFormData({ ...formData, Descricao: e.target.value })}
                            error={!!errors.Descricao}
                            helperText={errors.Descricao}
                            placeholder="Ex: Placa do veículo"
                        />

                        <FormControl fullWidth>
                            <InputLabel>Tipo *</InputLabel>
                            <Select
                                value={formData.Tipo}
                                label="Tipo *"
                                onChange={(e) => {
                                    const newTipo = e.target.value as FieldType;
                                    const defaultMask = getDefaultMaskForType(newTipo);
                                    
                                    // Se houver máscara padrão e não houver máscara customizada, aplicar a padrão
                                    if (defaultMask && (!formData.Mascara || selectedMaskType === 'CUSTOM')) {
                                        const maskType = identifyMaskType(defaultMask);
                                        setSelectedMaskType(maskType);
                                        setFormData({ 
                                            ...formData, 
                                            Tipo: newTipo,
                                            Mascara: defaultMask
                                        });
                                    } else {
                                        // Manter a máscara atual ou limpar se não houver padrão
                                        setFormData({ 
                                            ...formData, 
                                            Tipo: newTipo,
                                            Mascara: defaultMask || formData.Mascara
                                        });
                                        if (!defaultMask && !formData.Mascara) {
                                            setSelectedMaskType('CUSTOM');
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="Texto">Texto</MenuItem>
                                <MenuItem value="Numérico">Numérico</MenuItem>
                                <MenuItem value="Decimal">Decimal</MenuItem>
                                <MenuItem value="Data">Data</MenuItem>
                                <MenuItem value="Booleano">Booleano</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Tamanho *"
                            type="number"
                            value={formData.Tamanho}
                            onChange={(e) => setFormData({ ...formData, Tamanho: parseInt(e.target.value) || 0 })}
                            error={!!errors.Tamanho}
                            helperText={errors.Tamanho || 'Tamanho máximo do campo'}
                            inputProps={{ min: 1, max: 1000 }}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Máscara Pré-configurada</InputLabel>
                            <Select
                                value={selectedMaskType}
                                label="Máscara Pré-configurada"
                                onChange={(e) => {
                                    const maskType = e.target.value;
                                    setSelectedMaskType(maskType);
                                    if (maskType === 'CUSTOM') {
                                        // Mantém a máscara customizada atual ou limpa se vazia
                                        if (!formData.Mascara) {
                                            setFormData({ ...formData, Mascara: '' });
                                        }
                                    } else {
                                        // Aplica a máscara pré-configurada
                                        const maskOption = PREDEFINED_MASKS[maskType];
                                        if (maskOption) {
                                            setFormData({ ...formData, Mascara: maskOption.value });
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="CUSTOM">
                                    <em>Máscara Customizada</em>
                                </MenuItem>
                                <Divider />
                                {Object.entries(PREDEFINED_MASKS).map(([key, option]) => (
                                    <MenuItem key={key} value={key}>
                                        {option.label} - {option.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label={selectedMaskType === 'CUSTOM' ? "Máscara Customizada" : "Máscara (Editável)"}
                            value={formData.Mascara}
                            onChange={(e) => {
                                setFormData({ ...formData, Mascara: e.target.value });
                                // Se o usuário editar manualmente, mudar para CUSTOM
                                if (selectedMaskType !== 'CUSTOM') {
                                    const isStillPredefined = Object.values(PREDEFINED_MASKS).some(
                                        opt => opt.value === e.target.value
                                    );
                                    if (!isStillPredefined) {
                                        setSelectedMaskType('CUSTOM');
                                    }
                                }
                            }}
                            error={!!errors.Mascara}
                            helperText={
                                errors.Mascara ||
                                (selectedMaskType === 'CUSTOM' 
                                    ? "Use padrões: 9 (número), A (letra), a (letra minúscula), * (qualquer caractere). Ex: AAA-0000"
                                    : selectedMaskType !== 'CUSTOM' && PREDEFINED_MASKS[selectedMaskType]
                                        ? `Padrão: ${PREDEFINED_MASKS[selectedMaskType].description}`
                                        : "Use padrões: 9 (número), A (letra), a (letra minúscula), * (qualquer caractere)")
                            }
                            placeholder="Ex: AAA-0000, 999.999.999-99, etc."
                            disabled={selectedMaskType !== 'CUSTOM' && formData.Tipo === 'Data' && selectedMaskType === 'DATA'}
                        />

                        {formData.Mascara && (
                            <Alert severity="info">
                                <Typography variant="caption" component="div">
                                    <strong>Padrões de máscara:</strong>
                                    <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                        <li><strong>9</strong> - Apenas números (0-9)</li>
                                        <li><strong>A</strong> - Apenas letras maiúsculas (A-Z)</li>
                                        <li><strong>a</strong> - Apenas letras minúsculas (a-z)</li>
                                        <li><strong>*</strong> - Qualquer caractere alfanumérico</li>
                                    </ul>
                                    {selectedMaskType !== 'CUSTOM' && PREDEFINED_MASKS[selectedMaskType] && (
                                        <div style={{ marginTop: '4px' }}>
                                            <strong>Máscara selecionada:</strong> {PREDEFINED_MASKS[selectedMaskType].description}
                                        </div>
                                    )}
                                </Typography>
                            </Alert>
                        )}

                        {/* Validação específica por tipo */}
                        {formData.Tipo === 'Numérico' || formData.Tipo === 'Decimal' ? (
                            <Alert severity="warning">
                                <Typography variant="caption">
                                    <strong>Nota:</strong> Para campos numéricos, apenas números serão aceitos automaticamente. 
                                    A máscara é opcional e serve apenas para formatação visual.
                                </Typography>
                            </Alert>
                        ) : formData.Tipo === 'Data' && !formData.Mascara ? (
                            <Alert severity="info">
                                <Typography variant="caption">
                                    <strong>Sugestão:</strong> Para campos de data, recomenda-se usar a máscara de data pré-configurada.
                                </Typography>
                            </Alert>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSaveField}
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? 'Salvando...' : editingField ? 'Atualizar' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FieldConfiguration;

